import { createId } from '@paralleldrive/cuid2';
import type { WidgetGridLayout } from '@trinity/chat';
import type { BlockConfig, BlockItem, BlockWidget, DashboardConfig } from '@trinity/chat/types/dashboard-config';
import { upgradeLegacyLibraryRefs } from '@trinity/chat/utils/library-widgets';
import { getSectionEffectiveRows } from '../../components/PageDashboard/Builder/dashboardLayoutUtils.js';
import type { AddWidgetToDashboardRequest, PlacementBlock } from './Context';

type PlacementTarget = Omit<AddWidgetToDashboardRequest, 'segmentId'>;

// ─── Page-builder dashboards (project segments) ───────────────────────────────

function tagNameOf(block: BlockConfig): string | undefined {
  const tag = block.metadata?.tag;
  if (typeof tag === 'object' && tag !== null && 'tagName' in tag && typeof tag.tagName === 'string') {
    return tag.tagName;
  }
  return undefined;
}

export function getPagePlacementBlocks(config: DashboardConfig): PlacementBlock[] {
  return config.blocks.map((block) => ({
    id: block.id,
    title: block.title || tagNameOf(block) || 'Untitled block',
    sections: block.items.flatMap((item) =>
      item.type === 'section' ? [{ id: item.section.id, title: item.section.title || 'Untitled section' }] : [],
    ),
    allowBlockLevel: true,
  }));
}

/**
 * Row to place a new full-width item at, shifting existing items down when inserting before
 * the row at `insertPosition` (0-based among occupied start rows; -1 or out of range appends).
 * Never lands inside an item that spans several rows (`rowSpanOf`) — it inserts above it instead.
 */
function placeRow<T extends { row: number }>(
  items: T[],
  insertPosition: number,
  lastRow: number,
  rowSpanOf: (item: T) => number = () => 1,
): { row: number; shifted: T[] } {
  const startRows = [...new Set(items.map((item) => item.row))].sort((a, b) => a - b);
  if (insertPosition < 0 || insertPosition >= startRows.length) {
    return { row: lastRow + 1, shifted: items };
  }
  let row = startRows[insertPosition];
  for (;;) {
    const covering = items.find((item) => item.row < row && item.row + rowSpanOf(item) - 1 >= row);
    if (!covering) break;
    row = covering.row;
  }
  return { row, shifted: items.map((item) => (item.row >= row ? { ...item, row: item.row + 1 } : item)) };
}

function segWidget(widgetId: number): BlockWidget['widget'] {
  return { id: createId(), type: 'library_widget', config: { title: '', widgetId, source: 'project_library' } };
}

/**
 * Returns a copy of `config` with a `library_widget` pointer to `widgetId` placed at the target.
 * Legacy pointers in the page are upgraded too, since the result is cached under the dashboard's key.
 */
export function insertSegWidget(stored: DashboardConfig, target: PlacementTarget): DashboardConfig {
  const config = upgradeLegacyLibraryRefs(stored);
  const block = config.blocks.find((b) => b.id === target.blockId);
  if (!block) throw new Error(`Block "${target.blockId}" not found on the segment page.`);

  let items: BlockItem[];
  if (target.sectionId) {
    const sectionItem = block.items.find((item) => item.type === 'section' && item.section.id === target.sectionId);
    if (!sectionItem || sectionItem.type !== 'section') {
      throw new Error(`Section "${target.sectionId}" not found in block "${target.blockId}".`);
    }
    const widgets = sectionItem.section.widgets;
    const lastRow = Math.max(0, ...widgets.map((w) => w.row));
    const { row, shifted } = placeRow(widgets, target.insertPosition, lastRow);
    const updatedSection = {
      ...sectionItem,
      section: {
        ...sectionItem.section,
        widgets: [...shifted, { row, slot: 1, span: sectionItem.span, widget: segWidget(target.widgetId) }],
      },
    };
    items = block.items.map((item) => (item === sectionItem ? updatedSection : item));
  } else {
    // Rows an item occupies on the canvas: a widget's rowSpan, or a section's inner-row height
    // (same calculation WidgetCardShell uses), so new rows never land inside either.
    const rowSpanOf = (item: BlockItem) =>
      item.type === 'section'
        ? getSectionEffectiveRows(item.section, Math.max(1, Math.min(block.w, item.span)))
        : (item.rowSpan ?? 1);
    const lastRow = Math.max(0, ...block.items.map((item) => item.row + rowSpanOf(item) - 1));
    const { row, shifted } = placeRow(block.items, target.insertPosition, lastRow, rowSpanOf);
    items = [...shifted, { type: 'widget', row, slot: 1, span: block.w, widget: segWidget(target.widgetId) }];
  }

  return { ...config, blocks: config.blocks.map((b) => (b === block ? { ...block, items } : b)) };
}

// ─── WidgetGrid layouts (hackathon segments_v2) ───────────────────────────────

export function getLayoutPlacementBlocks(layout: WidgetGridLayout): PlacementBlock[] {
  return layout.blocks
    .filter((block) => block.sections && block.sections.length > 0)
    .map((block) => ({
      id: block.id,
      title: block.title?.trim() || block.tag?.tagName || `Block ${block.id.slice(-4)}`,
      sections: (block.sections ?? []).map((section) => ({
        id: section.id,
        title: section.title ?? section.tag?.tagName ?? `Section ${section.id.slice(-4)}`,
      })),
      allowBlockLevel: false,
    }));
}

/** Returns a copy of `layout` with `widgetId` inserted into the target section's content items. */
export function insertLayoutWidget(layout: WidgetGridLayout, target: PlacementTarget): WidgetGridLayout {
  let sectionFound = false;
  const blocks = layout.blocks.map((block) => ({
    ...block,
    sections: (block.sections ?? []).map((section) => {
      if (section.id !== target.sectionId) return section;
      sectionFound = true;
      const contentItems = [...section.contentItems];
      const item = { type: 'widget' as const, widgetId: target.widgetId };
      if (target.insertPosition >= 0 && target.insertPosition <= contentItems.length) {
        contentItems.splice(target.insertPosition, 0, item);
      } else {
        contentItems.push(item);
      }
      return { ...section, contentItems };
    }),
  }));
  if (!sectionFound) throw new Error(`Section "${target.sectionId ?? ''}" not found in the dashboard.`);
  return { blocks };
}
